/**
 * EditarEditalPage — edição das 15 seções do Edital (fase Edital).
 *
 * Réplica do edit_edital.html. A edição preserva o metadata da geração
 * (número, modalidade) — o legado o descartava ao salvar.
 */

import {
  faArrowLeft,
  faExclamationTriangle,
  faSave,
  faScroll,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { documentoEdital, formularioEdital, salvarEdital } from '../../api/edital'
import { useToast } from '../../components/Toast'

export function EditarEditalPage() {
  const { id } = useParams()
  const idNumerico = Number(id)
  const navegar = useNavigate()
  const clienteQuery = useQueryClient()
  const { notificar } = useToast()
  const [secoes, setSecoes] = useState<Record<string, string> | null>(null)

  const { data, isLoading, error } = useQuery({
    queryKey: ['edital-formulario', idNumerico],
    queryFn: () => formularioEdital(idNumerico),
    enabled: Number.isFinite(idNumerico),
    retry: false,
  })
  // os títulos vêm do documento: uma fonte só de verdade
  const { data: documento } = useQuery({
    queryKey: ['edital-documento', idNumerico],
    queryFn: () => documentoEdital(idNumerico),
    enabled: Number.isFinite(idNumerico),
    retry: false,
  })

  useEffect(() => {
    if (data) setSecoes(data.secoes)
  }, [data])

  const salvar = useMutation({
    mutationFn: () => salvarEdital(idNumerico, secoes!),
    onSuccess: (atualizado) => {
      clienteQuery.setQueryData(['edital-documento', idNumerico], atualizado)
      void clienteQuery.invalidateQueries({ queryKey: ['processos-licitatorios'] })
      notificar('success', 'Edital atualizado com sucesso!')
      navegar(`/edital/${idNumerico}`)
    },
    onError: (e: Error) => notificar('error', e.message),
  })

  if (isLoading) {
    return (
      <p className="mt-12 text-center text-slate-500">
        <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
        Carregando Edital…
      </p>
    )
  }
  if (error || !data || !secoes) {
    return (
      <div className="mx-auto mt-12 max-w-xl rounded-2xl bg-white p-8 text-center shadow-lg dark:bg-dark-card">
        <h1 className="mb-2 text-2xl font-semibold text-slate-700 dark:text-slate-200">
          Não foi possível abrir a edição
        </h1>
        <p className="mb-6 text-slate-500">{(error as Error)?.message ?? 'Edital não encontrado.'}</p>
        <Link to={`/edital/${idNumerico}`} className="inline-flex items-center rounded-lg bg-slate-600 px-6 py-3 font-semibold text-white hover:bg-slate-700">
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Voltar ao documento
        </Link>
      </div>
    )
  }

  const lista = documento?.secoes ?? []

  return (
    <form
      className="mx-auto max-w-5xl"
      onSubmit={(e) => {
        e.preventDefault()
        salvar.mutate()
      }}
    >
      <header className="mb-8 text-center">
        <h1 className="mb-2 text-3xl font-bold text-slate-800 dark:text-slate-100">
          <FontAwesomeIcon icon={faScroll} className="mr-3 text-purple-600" />
          Editar Edital
        </h1>
        <p className="text-slate-600 dark:text-slate-400">{data.objeto}</p>
      </header>

      {lista.map((secao) => (
        <fieldset key={secao.chave} className="mb-6 rounded-xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-dark-card">
          <legend className="px-2 text-lg font-semibold text-slate-800 dark:text-slate-100">
            {secao.titulo}
          </legend>
          <label htmlFor={`secao_${secao.chave}`} className="sr-only">
            {secao.titulo}
          </label>
          <textarea
            id={`secao_${secao.chave}`}
            name={`secao_${secao.chave}`}
            rows={10}
            value={secoes[secao.chave] ?? ''}
            onChange={(e) =>
              setSecoes((atual) => (atual ? { ...atual, [secao.chave]: e.target.value } : atual))
            }
            className="w-full rounded-lg border border-slate-300 px-4 py-2 font-mono text-sm leading-relaxed text-slate-800 focus:outline-none focus:ring-2 focus:ring-purple-500 dark:border-slate-600 dark:bg-dark-bg dark:text-slate-100"
          />
        </fieldset>
      ))}

      <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
        <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
        Seções em branco saem do documento — inclusive do DOCX exportado.
      </div>

      <div className="flex flex-col justify-end gap-3 pb-4 sm:flex-row">
        <Link to={`/edital/${idNumerico}`} className="inline-flex items-center justify-center rounded-xl bg-slate-200 px-6 py-3 font-semibold text-slate-700 transition-colors hover:bg-slate-300 dark:bg-dark-hover dark:text-slate-200">
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Cancelar
        </Link>
        <button type="submit" disabled={salvar.isPending} className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-purple-600 to-violet-600 px-6 py-3 font-semibold text-white shadow-lg transition-all hover:from-purple-700 hover:to-violet-700 disabled:opacity-60">
          <FontAwesomeIcon icon={salvar.isPending ? faSpinner : faSave} spin={salvar.isPending} className="mr-2" />
          Salvar Alterações
        </button>
      </div>
    </form>
  )
}
